import { useState, useMemo } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/auth.context";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertCircle,
  ArrowLeft,
  Search,
  Users,
  CreditCard,
  TrendingUp,
  Eye,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { format } from "date-fns";

export default function SelectInsurers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch loans where current user is an insurer
  const { data: insuredLoansData, isLoading, refetch } = useQuery(
    ["insured-loans", user?.id],
    async () => {
      const { data } = await api.get("/loans/insured");
      return data.data;
    },
    {
      enabled: !!user?.id,
    }
  );

  const insuredLoans = insuredLoansData?.insuredLoans || [];
  const activeInsuredLoans = insuredLoansData?.activeInsuredLoans || 0;
  const remainingCapacity = insuredLoansData?.remainingCapacity || 4;
  const maxLoansPerInsurer = insuredLoansData?.maxLoansPerInsurer || 4;
  const canInsureMore = insuredLoansData?.canInsureMore ?? true;

  // Filter loans by search query
  const filteredLoans = useMemo(() => {
    if (!searchQuery) return insuredLoans;
    const query = searchQuery.toLowerCase();
    return insuredLoans.filter((loan: any) => {
      return (
        loan.borrower?.name?.toLowerCase().includes(query) ||
        loan.borrower?.email?.toLowerCase().includes(query) ||
        loan.borrower?.phone?.toLowerCase().includes(query) ||
        loan.purpose?.toLowerCase().includes(query)
      );
    });
  }, [insuredLoans, searchQuery]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "Pending":
        return <Badge variant="secondary" className="bg-orange-100 text-orange-800">Pending</Badge>;
      case "Approved":
        return <Badge variant="default" className="bg-blue-100 text-blue-800">Approved</Badge>;
      case "Active":
        return <Badge variant="default" className="bg-green-100 text-green-800">Active</Badge>;
      case "Completed":
        return <Badge variant="outline" className="bg-gray-100 text-gray-800">Completed</Badge>;
      case "Rejected":
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (!user?.id) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-lg font-semibold">Authentication Required</p>
          <p className="text-muted-foreground mt-2">
            Please log in to view your insured loans.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex-1">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-theme-text">
            Insurers
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            Track loans you are insuring and monitor payment progress
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate("/member/dashboard")}
          className="flex items-center gap-2 w-full sm:w-auto"
        >
          <ArrowLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          <span className="text-xs sm:text-sm">Back to Dashboard</span>
        </Button>
      </div>

      {/* Insurer Capacity Alert */}
      <div className={`p-4 rounded-lg border ${canInsureMore ? "bg-blue-50 border-blue-200" : "bg-orange-50 border-orange-200"}`}>
        {canInsureMore ? (
          <span className="text-black">
            You are currently insuring <strong>{activeInsuredLoans}</strong> active loan(s). 
            You can insure up to <strong>{maxLoansPerInsurer}</strong> loans at a time. 
            <strong> {remainingCapacity}</strong> slot(s) remaining.
          </span>
        ) : (
          <span className="text-black">
            <strong>You have reached the insurer limit of {maxLoansPerInsurer} loans.</strong> 
            You cannot insure more loans until some of your current insured loans are completed or rejected. 
            If another member tries to select you as an insurer, they will receive a message that you have reached your limit.
          </span>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Card className="group relative overflow-hidden border-2 border-[#4dbd07] bg-white shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:border-[#3d9a06]">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300">
                Active Insured Loans
              </CardTitle>
              <CreditCard className="h-4 w-4 sm:h-5 sm:w-5 text-[#4dbd07] group-hover:scale-110 transition-transform duration-300" />
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-2 mb-2">
                <div className="text-2xl sm:text-3xl font-bold text-[#4dbd07]">
                  {activeInsuredLoans}
                </div>
                <span className="text-sm font-semibold text-[#4dbd07]">/{maxLoansPerInsurer}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <TrendingUp className="h-3 w-3 text-[#4dbd07]" />
                <p className="text-[10px] sm:text-xs font-medium text-gray-600 dark:text-gray-400">
                  {remainingCapacity} slot(s) available
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <Card className="group relative overflow-hidden border-2 border-[#4dbd07] bg-white shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:border-[#3d9a06]">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300">
                Total Coverage Amount
              </CardTitle>
              <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-[#4dbd07] group-hover:scale-110 transition-transform duration-300" />
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-2 mb-2">
                <div className="text-2xl sm:text-3xl font-bold text-[#4dbd07]">
                  {insuredLoans
                    .filter((loan: any) => ["Pending", "Approved", "Active"].includes(loan.status))
                    .reduce((sum: number, loan: any) => sum + (loan.coverageAmount || 0), 0)
                    .toLocaleString()}
                </div>
                <span className="text-sm font-semibold text-[#4dbd07]">FRW</span>
              </div>
              <div className="flex items-center gap-1.5">
                <p className="text-[10px] sm:text-xs font-medium text-gray-600 dark:text-gray-400">
                  Total coverage liability
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <Card className="group relative overflow-hidden border-2 border-[#4dbd07] bg-white shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:border-[#3d9a06]">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300">
                Total Insured Loans
              </CardTitle>
              <Users className="h-4 w-4 sm:h-5 sm:w-5 text-[#4dbd07] group-hover:scale-110 transition-transform duration-300" />
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-2 mb-2">
                <div className="text-2xl sm:text-3xl font-bold text-[#4dbd07]">
                  {insuredLoansData?.totalInsuredLoans || 0}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <Users className="h-3 w-3 text-[#4dbd07]" />
                <p className="text-[10px] sm:text-xs font-medium text-gray-600 dark:text-gray-400">
                  All time
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by borrower name, email, phone, or loan purpose..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Insured Loans List */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Insured Loans ({filteredLoans.length})
            </CardTitle>
            <CardDescription>
              Loans where you are acting as an insurer - track payment progress
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4dbd07]"></div>
              </div>
            ) : filteredLoans.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {searchQuery
                    ? "No loans found matching your search"
                    : "You are not currently insuring any loans"}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredLoans.map((loan: any) => (
                  <motion.div
                    key={loan.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 border rounded-lg hover:shadow-md transition-shadow"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <CreditCard className="h-4 w-4 text-[#4dbd07]" />
                          <p className="font-semibold text-sm sm:text-base">
                            Loan #{loan.id} - {loan.borrower?.name}
                          </p>
                          {getStatusBadge(loan.status)}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Applied: {loan.applicationDate ? format(new Date(loan.applicationDate), "MMM dd, yyyy") : "N/A"}
                        </p>
                        {loan.purpose && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Purpose: {loan.purpose}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-lg sm:text-xl font-bold text-[#4dbd07]">
                          {loan.loanAmount.toLocaleString()} FRW
                        </p>
                        <p className="text-xs text-muted-foreground">Loan Amount</p>
                      </div>
                    </div>

                    {/* Coverage Information */}
                    <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between text-xs sm:text-sm mb-1">
                        <span className="text-muted-foreground">Your Coverage</span>
                        <span className="font-semibold text-blue-700">
                          {loan.coverageAmount.toLocaleString()} FRW ({(loan.coverageRatio * 100).toFixed(1)}%)
                        </span>
                      </div>
                    </div>

                    {/* Payment Progress */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs sm:text-sm">
                        <span className="text-muted-foreground">Payment Progress</span>
                        <span className="font-semibold text-[#4dbd07]">
                          {loan.progressPercentage.toFixed(1)}%
                        </span>
                      </div>
                      <Progress value={loan.progressPercentage} className="h-2" />
                      
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2">
                        <div>
                          <p className="text-xs text-muted-foreground">Paid</p>
                          <p className="text-sm font-semibold text-green-600">
                            {loan.totalPaid.toLocaleString()} FRW
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Remaining</p>
                          <p className="text-sm font-semibold text-orange-600">
                            {loan.remainingAmount.toLocaleString()} FRW
                          </p>
                        </div>
                        {loan.monthlyPayment > 0 && (
                          <div>
                            <p className="text-xs text-muted-foreground">Monthly Payment</p>
                            <p className="text-sm font-semibold">
                              {loan.monthlyPayment.toLocaleString()} FRW
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/member/loans`)}
                        className="w-full sm:w-auto"
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        View Loan Details
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
